import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import MysticalParticles from './MysticalParticles';

const TestCard = ({ title, section, chips, prompt, description }) => {
  return (
    <div className="mystical-card group transition-all duration-300 hover:shadow-purple-700/10 border-2 border-purple-400 bg-gradient-to-br from-purple-200 to-pink-100">
      <div className="p-4">
        {/* Blank card label */}
        <div className="mb-2 bg-purple-700 text-white text-xs font-bold px-2 py-1 rounded-full text-center">
          BLANK CARD
        </div>
        
        <h4 className="mystical-subtitle text-black text-xl mb-3">{title}</h4>
        <div className="mb-2 bg-blue-100 text-blue-800 text-sm font-semibold px-2 py-1 rounded">
          Section: {section}
        </div>
        
        {prompt && (
          <div className="mystical-card bg-purple-700 bg-opacity-10 mb-3">
            <p className="mystical-text text-gray-800 text-sm italic leading-relaxed">
              [ {prompt} ]
            </p>
          </div>
        )}
        
        {description && (
          <p className="mystical-text text-gray-800 mb-4 text-sm leading-relaxed">
            {description}
          </p>
        )}
        
        {chips && (
          <div className="mystical-card bg-purple-700 bg-opacity-20">
            <p className="mystical-text text-purple-800 font-bold">
              CHIPS: {chips}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const HolidayCard = ({ title, imgUrl, prompt, description, seasonalType, chips }) => {
  const getHolidayColors = (type) => {
    switch (type) {
      case 'thanksgiving':
        return {
          gradient: 'from-amber-600 via-orange-700 to-red-800',
          border: 'border-amber-500',
          text: 'text-amber-100',
          bg: 'rgba(146, 64, 14, 0.3)',
          icon: '🦃'
        };
      case 'christmas':
        return {
          gradient: 'from-red-600 via-green-700 to-red-800',
          border: 'border-red-500',
          text: 'text-red-100',
          bg: 'rgba(185, 28, 28, 0.3)',
          icon: '🎄'
        };
      case 'new-years':
        return {
          gradient: 'from-gray-600 via-blue-700 to-purple-800',
          border: 'border-purple-500',
          text: 'text-purple-100',
          bg: 'rgba(88, 28, 135, 0.3)',
          icon: '🎆'
        };
      case 'valentines':
        return {
          gradient: 'from-pink-600 via-red-700 to-pink-800',
          border: 'border-pink-500',
          text: 'text-pink-100',
          bg: 'rgba(217, 70, 239, 0.3)',
          icon: '💕'
        };
      case 'easter':
        return {
          gradient: 'from-purple-600 via-pink-700 to-yellow-600',
          border: 'border-purple-500',
          text: 'text-purple-100',
          bg: 'rgba(126, 34, 206, 0.3)',
          icon: '🐰'
        };
      case 'summer':
        return {
          gradient: 'from-yellow-600 via-orange-700 to-red-600',
          border: 'border-yellow-500',
          text: 'text-yellow-100',
          bg: 'rgba(202, 138, 4, 0.3)',
          icon: '☀️'
        };
      case 'fourth-of-july':
        return {
          gradient: 'from-red-600 via-white to-blue-600',
          border: 'border-red-500',
          text: 'text-red-100',
          bg: 'rgba(185, 28, 28, 0.3)',
          icon: '🇺🇸'
        };
      case 'st-patricks':
        return {
          gradient: 'from-green-600 via-green-700 to-green-800',
          border: 'border-green-500',
          text: 'text-green-100',
          bg: 'rgba(34, 197, 94, 0.3)',
          icon: '🍀'
        };
      case 'mothers-day':
        return {
          gradient: 'from-pink-600 via-purple-700 to-pink-800',
          border: 'border-pink-500',
          text: 'text-pink-100',
          bg: 'rgba(219, 39, 119, 0.3)',
          icon: '🌸'
        };
      case 'fathers-day':
        return {
          gradient: 'from-blue-600 via-gray-700 to-blue-800',
          border: 'border-blue-500',
          text: 'text-blue-100',
          bg: 'rgba(37, 99, 235, 0.3)',
          icon: '👔'
        };
      case 'memorial-day':
        return {
          gradient: 'from-red-600 via-white to-blue-600',
          border: 'border-red-500',
          text: 'text-red-100',
          bg: 'rgba(185, 28, 28, 0.3)',
          icon: '🪖'
        };
      case 'labor-day':
        return {
          gradient: 'from-gray-600 via-blue-700 to-gray-800',
          border: 'border-gray-500',
          text: 'text-gray-100',
          bg: 'rgba(75, 85, 99, 0.3)',
          icon: '🔨'
        };
      case 'veterans-day':
        return {
          gradient: 'from-red-600 via-white to-blue-600',
          border: 'border-red-500',
          text: 'text-red-100',
          bg: 'rgba(185, 28, 28, 0.3)',
          icon: '🎖️'
        };
      case 'earth-day':
        return {
          gradient: 'from-green-600 via-blue-700 to-green-800',
          border: 'border-green-500',
          text: 'text-green-100',
          bg: 'rgba(34, 197, 94, 0.3)',
          icon: '🌍'
        };
      default:
        return {
          gradient: 'from-purple-600 via-purple-700 to-purple-800',
          border: 'border-purple-500',
          text: 'text-purple-100',
          bg: 'rgba(88, 28, 135, 0.3)',
          icon: '🎉'
        };
    }
  };

  const colors = getHolidayColors(seasonalType);

  return (
    <div className="relative group transition-all duration-300 border-2 shadow-lg hover:shadow-purple-400/30 backdrop-filter-blur rounded-2xl p-6" 
         style={{
           background: `linear-gradient(135deg, ${colors.bg} 0%, rgba(0, 0, 0, 0.6) 50%, ${colors.bg} 100%)`,
           borderColor: colors.border.replace('border-', '')
         }}>
      
      {/* Holiday decorative elements */}
      <div className="absolute top-4 left-4 text-2xl opacity-15">{colors.icon}</div>
      <div className="absolute top-4 right-4 text-2xl opacity-15">{colors.icon}</div>
      <div className="absolute bottom-4 left-4 text-2xl opacity-15">{colors.icon}</div>
      <div className="absolute bottom-4 right-4 text-2xl opacity-15">{colors.icon}</div>
      
      {/* Image setup (if IMG_URL is provided, it will be used); default/placeholder otherwise */}
      {imgUrl ? (
        <div className="mb-4 flex justify-center relative">
          <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient} rounded-lg blur-sm`}></div>
          <img 
            src={imgUrl} 
            alt={title} 
            className={`relative w-48 h-48 object-cover rounded-lg border-2 shadow-lg filter brightness-90 sepia-10 border-${colors.border.replace('border-', '')}`}
          />
        </div>
      ) : (
        <div className={`mb-4 flex justify-center relative w-48 h-48 mx-auto bg-gradient-to-br ${colors.gradient} rounded-lg border-2 flex items-center justify-center border-${colors.border.replace('border-', '')}`}>
          <span className="text-6xl">{colors.icon}</span>
        </div>
      )}
      
      <h4 className={`mystical-subtitle ${colors.text} text-xl mb-3 font-bold text-center drop-shadow-md`}>
        {title}
      </h4>
      
      {prompt && (
        <div className={`mystical-card bg-opacity-40 border mb-3 border-${colors.border.replace('border-', '')}`} style={{backgroundColor: colors.bg}}>
          <p className={`mystical-text text-sm italic leading-relaxed ${colors.text}`}>
            [ {prompt} ]
          </p>
        </div>
      )}
      
      {description && (
        <p className={`mystical-text mb-4 text-sm leading-relaxed text-center ${colors.text}`}>
          {description}
        </p>
      )}
      
      {chips && (
        <div className={`mystical-card bg-opacity-60 border`} 
             style={{
               background: colors.gradient,
               borderColor: colors.border.replace('border-', '')
             }}>
          <p className={`p-1 text-center font-bold ${colors.text}`}>
            {colors.icon} Chips: {chips} {colors.icon}
          </p>
        </div>
      )}
      
      {/* Holiday bottom decoration */}
      <div className="mt-4 text-center">
        <p className={`${colors.text} font-semibold text-lg drop-shadow-md`}>
          {colors.icon} {seasonalType.replace('-', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} Special {colors.icon}
        </p>
      </div>
    </div>
  );
};

const SectionHeader = ({ title }) => {
  return (
    <div className="mystical-card bg-purple-800 bg-opacity-20 mb-6">
      <h3 className="mystical-subtitle text-black text-2xl text-center">{title}</h3>
    </div>
  );
};

const HolidaySection = ({ title, seasonalType, cards, timeLeft }) => {
  const getColors = (type) => {
    switch (type) {
      case 'thanksgiving':
        return { 
          icon: '🦃', 
          gradient: 'from-amber-600 via-orange-700 to-red-800',
          border: 'border-amber-500',
          text: 'text-amber-100',
          bg: 'rgba(146, 64, 14, 0.3)',
          primary: 'amber',
          secondary: 'orange',
          tertiary: 'red'
        };
      case 'christmas':
        return { 
          icon: '🎄', 
          gradient: 'from-red-600 via-green-700 to-red-800',
          border: 'border-red-500',
          text: 'text-red-100',
          bg: 'rgba(185, 28, 28, 0.3)',
          primary: 'red',
          secondary: 'green',
          tertiary: 'red'
        };
      case 'new-years':
        return { 
          icon: '🎆', 
          gradient: 'from-gray-600 via-blue-700 to-purple-800',
          border: 'border-purple-500',
          text: 'text-purple-100',
          bg: 'rgba(88, 28, 135, 0.3)',
          primary: 'purple',
          secondary: 'blue',
          tertiary: 'gray'
        };
      case 'valentines':
        return { 
          icon: '💕', 
          gradient: 'from-pink-600 via-red-700 to-pink-800',
          border: 'border-pink-500',
          text: 'text-pink-100',
          bg: 'rgba(217, 70, 239, 0.3)',
          primary: 'pink',
          secondary: 'red',
          tertiary: 'pink'
        };
      case 'easter':
        return { 
          icon: '🐰', 
          gradient: 'from-purple-600 via-pink-700 to-yellow-600',
          border: 'border-purple-500',
          text: 'text-purple-100',
          bg: 'rgba(126, 34, 206, 0.3)',
          primary: 'purple',
          secondary: 'pink',
          tertiary: 'yellow'
        };
      case 'summer':
        return { 
          icon: '☀️', 
          gradient: 'from-yellow-600 via-orange-700 to-red-600',
          border: 'border-yellow-500',
          text: 'text-yellow-100',
          bg: 'rgba(202, 138, 4, 0.3)',
          primary: 'yellow',
          secondary: 'orange',
          tertiary: 'red'
        };
      case 'fourth-of-july':
        return { 
          icon: '🇺🇸', 
          gradient: 'from-red-600 via-white to-blue-600',
          border: 'border-red-500',
          text: 'text-red-100',
          bg: 'rgba(185, 28, 28, 0.3)',
          primary: 'red',
          secondary: 'white',
          tertiary: 'blue'
        };
      case 'st-patricks':
        return { 
          icon: '🍀', 
          gradient: 'from-green-600 via-green-700 to-green-800',
          border: 'border-green-500',
          text: 'text-green-100',
          bg: 'rgba(34, 197, 94, 0.3)',
          primary: 'green',
          secondary: 'green',
          tertiary: 'green'
        };
      case 'mothers-day':
        return { 
          icon: '🌸', 
          gradient: 'from-pink-600 via-purple-700 to-pink-800',
          border: 'border-pink-500',
          text: 'text-pink-100',
          bg: 'rgba(219, 39, 119, 0.3)',
          primary: 'pink',
          secondary: 'purple',
          tertiary: 'pink'
        };
      case 'fathers-day':
        return { 
          icon: '👔', 
          gradient: 'from-blue-600 via-gray-700 to-blue-800',
          border: 'border-blue-500',
          text: 'text-blue-100',
          bg: 'rgba(37, 99, 235, 0.3)',
          primary: 'blue',
          secondary: 'gray',
          tertiary: 'blue'
        };
      case 'memorial-day':
        return { 
          icon: '🪖', 
          gradient: 'from-red-600 via-white to-blue-600',
          border: 'border-red-500',
          text: 'text-red-100',
          bg: 'rgba(185, 28, 28, 0.3)',
          primary: 'red',
          secondary: 'white',
          tertiary: 'blue'
        };
      case 'labor-day':
        return { 
          icon: '🔨', 
          gradient: 'from-gray-600 via-blue-700 to-gray-800',
          border: 'border-gray-500',
          text: 'text-gray-100',
          bg: 'rgba(75, 85, 99, 0.3)',
          primary: 'gray',
          secondary: 'blue',
          tertiary: 'gray'
        };
      case 'veterans-day':
        return { 
          icon: '🎖️', 
          gradient: 'from-red-600 via-white to-blue-600',
          border: 'border-red-500',
          text: 'text-red-100',
          bg: 'rgba(185, 28, 28, 0.3)',
          primary: 'red',
          secondary: 'white',
          tertiary: 'blue'
        };
      case 'earth-day':
        return { 
          icon: '🌍', 
          gradient: 'from-green-600 via-blue-700 to-green-800',
          border: 'border-green-500',
          text: 'text-green-100',
          bg: 'rgba(34, 197, 94, 0.3)',
          primary: 'green',
          secondary: 'blue',
          tertiary: 'green'
        };
      case 'halloween':
        return { 
          icon: '🎃', 
          gradient: 'from-orange-600 via-orange-700 to-amber-700',
          border: 'border-orange-500',
          text: 'text-orange-100',
          bg: 'rgba(234, 88, 12, 0.3)',
          primary: 'orange',
          secondary: 'amber',
          tertiary: 'orange'
        };
      default:
        return { 
          icon: '🎉', 
          gradient: 'from-purple-600 via-purple-700 to-purple-800',
          border: 'border-purple-500',
          text: 'text-purple-100',
          bg: 'rgba(88, 28, 135, 0.3)',
          primary: 'purple',
          secondary: 'purple',
          tertiary: 'purple'
        };
    }
  };

  const colors = getColors(seasonalType);
  const isActiveHoliday = timeLeft.nextHoliday === seasonalType;

  return (
    <div className="mb-12 relative">
      {/* Themed background effects using card colors */}
      <div 
        className="absolute inset-0 rounded-2xl"
        style={{
          background: `linear-gradient(135deg, ${colors.bg} 0%, rgba(0, 0, 0, 0.6) 50%, ${colors.bg} 100%)`
        }}
      ></div>
      
      {/* Icon decorations at corners */}
      <div className={`absolute top-4 left-4 text-2xl opacity-15 text-${colors.primary}-600`}>{colors.icon}</div>
      <div className={`absolute top-4 right-4 text-2xl opacity-15 text-${colors.primary}-600`}>{colors.icon}</div>
      <div className={`absolute bottom-4 left-4 text-2xl opacity-15 text-${colors.primary}-600`}>{colors.icon}</div>
      <div className={`absolute bottom-4 right-4 text-2xl opacity-15 text-${colors.primary}-600`}>{colors.icon}</div>

      {/* Main header with countdown timer */}
      <div className="relative z-10 mb-8">
        <div 
          className="mystical-card bg-opacity-70 border-2 shadow-lg shadow-purple-500/20"
          style={{
            background: `linear-gradient(135deg, ${colors.bg} 0%, rgba(0, 0, 0, 0.6) 50%, ${colors.bg} 100%)`,
            borderColor: colors.border.replace('border-', '')
          }}
        >
          <h3 className={`mystical-subtitle ${colors.text} text-3xl text-center font-bold tracking-wider drop-shadow-lg`}>
            {colors.icon} {title.toUpperCase()} {colors.icon}
          </h3>
          
          {/* Countdown Timer - only show for active holiday */}
          {isActiveHoliday && (
            <div className="mt-6 grid grid-cols-4 gap-4 max-w-md mx-auto">
              <div className="text-center">
                <div 
                  className="rounded-lg p-3 border"
                  style={{
                    backgroundColor: colors.bg,
                    borderColor: colors.border.replace('border-', '')
                  }}
                >
                  <div className={`${colors.text} text-2xl font-bold`}>{timeLeft.days || 0}</div>
                  <div className={`${colors.text} text-sm opacity-80`}>Days</div>
                </div>
              </div>
              <div className="text-center">
                <div 
                  className="rounded-lg p-3 border"
                  style={{
                    backgroundColor: colors.bg,
                    borderColor: colors.border.replace('border-', '')
                  }}
                >
                  <div className={`${colors.text} text-2xl font-bold`}>{timeLeft.hours || 0}</div>
                  <div className={`${colors.text} text-sm opacity-80`}>Hours</div>
                </div>
              </div>
              <div className="text-center">
                <div 
                  className="rounded-lg p-3 border"
                  style={{
                    backgroundColor: colors.bg,
                    borderColor: colors.border.replace('border-', '')
                  }}
                >
                  <div className={`${colors.text} text-2xl font-bold`}>{timeLeft.minutes || 0}</div>
                  <div className={`${colors.text} text-sm opacity-80`}>Minutes</div>
                </div>
              </div>
              <div className="text-center">
                <div 
                  className="rounded-lg p-3 border"
                  style={{
                    backgroundColor: colors.bg,
                    borderColor: colors.border.replace('border-', '')
                  }}
                >
                  <div className={`${colors.text} text-2xl font-bold`}>{timeLeft.seconds || 0}</div>
                  <div className={`${colors.text} text-sm opacity-80`}>Seconds</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cards Grid */}
      <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        {cards.map((card, index) => (
          <HolidayCard key={`${seasonalType}-${index}`} {...card} />
        ))}
      </div>

      {/* Bottom decoration */}
      <div className="relative z-10 mt-8 text-center">
        <p className={`${colors.text} font-semibold text-lg drop-shadow-md opacity-80`}>
          {colors.icon} Blank {seasonalType.replace('-', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} Design Template! {colors.icon}
        </p>
      </div>
    </div>
  );
};

const MystveilUpgradesDevsPage = () => {
  const [timeLeft, setTimeLeft] = useState({});

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const currentYear = now.getFullYear();
      
      // Define all holiday dates
      const holidays = {
        thanksgiving: new Date(currentYear, 10, 28, 23, 59, 59), // Fourth Thursday of November
        christmas: new Date(currentYear, 11, 25, 23, 59, 59), // December 25th
        newYears: new Date(currentYear + 1, 0, 1, 23, 59, 59), // January 1st next year
        valentines: new Date(currentYear + 1, 1, 14, 23, 59, 59), // February 14th next year
        easter: new Date(currentYear + 1, 3, 20, 23, 59, 59), // Approximate Easter date
        fourthOfJuly: new Date(currentYear + 1, 6, 4, 23, 59, 59), // July 4th next year
        stPatricks: new Date(currentYear + 1, 2, 17, 23, 59, 59), // March 17th next year
        mothersDay: new Date(currentYear + 1, 4, 12, 23, 59, 59), // Second Sunday of May
        fathersDay: new Date(currentYear + 1, 5, 16, 23, 59, 59), // Third Sunday of June
        memorialDay: new Date(currentYear + 1, 4, 26, 23, 59, 59), // Last Monday of May
        laborDay: new Date(currentYear + 1, 8, 2, 23, 59, 59), // First Monday of September
        veteransDay: new Date(currentYear + 1, 10, 11, 23, 59, 59), // November 11th next year
        earthDay: new Date(currentYear + 1, 3, 22, 23, 59, 59), // April 22nd next year
        halloween: new Date(currentYear, 9, 31, 23, 59, 59), // October 31st
        summer: new Date(currentYear + 1, 5, 21, 23, 59, 59), // Summer solstice
      };

      // Calculate time until next holiday
      let nextHoliday = null;
      let nextHolidayName = '';
      let minDifference = Infinity;

      Object.entries(holidays).forEach(([name, date]) => {
        // If holiday has passed this year, check next year
        if (now > date && name !== 'halloween') {
          date.setFullYear(currentYear + 1);
        }
        
        const difference = date - now;
        if (difference > 0 && difference < minDifference) {
          minDifference = difference;
          nextHoliday = date;
          nextHolidayName = name;
        }
      });

      if (nextHoliday) {
        const days = Math.floor(minDifference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((minDifference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((minDifference / 1000 / 60) % 60);
        const seconds = Math.floor((minDifference / 1000) % 60);

        return { 
          days, 
          hours, 
          minutes, 
          seconds, 
          nextHoliday: nextHolidayName 
        };
      } else {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, nextHoliday: 'halloween' };
      }
    };

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    setTimeLeft(calculateTimeLeft());

    return () => clearInterval(timer);
  }, []);

  // Blank cards for each section - just the designs
  const testUpgrades = {
    head: [
      {
        title: "Blank Card",
        section: "Head",
        chips: "---",
        description: "Blank card for design purposes",
        prompt: "Blank prompt placeholder"
      }
    ],
    ears: [
      {
        title: "Blank Card",
        section: "Ears",
        chips: "---",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes"
      }
    ],
    body: [
      {
        title: "Blank Card",
        section: "Body",
        chips: "---",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes"
      }
    ],
    legs: [
      {
        title: "Blank Card",
        section: "Legs",
        chips: "---",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes"
      }
    ],
    tail: [
      {
        title: "Blank Card",
        section: "Tail",
        chips: "---",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes"
      }
    ],
    markings: [
      {
        title: "Blank Card",
        section: "Markings",
        chips: "---",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes"
      }
    ],
    misc: [
      {
        title: "Blank Card",
        section: "Misc",
        description: "Blank card for design purposes"
      }
    ],
    halloween: [
      {
        title: "Blank Card",
        section: "Halloween",
        chips: "---",
        description: "Blank card for design purposes",
        imgUrl: undefined
      }
    ]
  };

  // Blank holiday cards - just the designs
  const holidayCards = {
    thanksgiving: [
      {
        title: "Blank Holiday Card",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes",
        imgUrl: undefined,
        seasonalType: "thanksgiving",
        chips: "---"
      }
    ],
    christmas: [
      {
        title: "Blank Holiday Card",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes",
        imgUrl: undefined,
        seasonalType: "christmas",
        chips: "---"
      }
    ],
    newYears: [
      {
        title: "Blank Holiday Card",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes",
        imgUrl: undefined,
        seasonalType: "new-years",
        chips: "---"
      }
    ],
    valentines: [
      {
        title: "Blank Holiday Card",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes",
        imgUrl: undefined,
        seasonalType: "valentines",
        chips: "---"
      }
    ],
    easter: [
      {
        title: "Blank Holiday Card",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes",
        imgUrl: undefined,
        seasonalType: "easter",
        chips: "---"
      }
    ],
    fourthOfJuly: [
      {
        title: "Blank Holiday Card",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes",
        imgUrl: undefined,
        seasonalType: "fourth-of-july",
        chips: "---"
      }
    ],
    stPatricks: [
      {
        title: "Blank Holiday Card",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes",
        imgUrl: undefined,
        seasonalType: "st-patricks",
        chips: "---"
      }
    ],
    mothersDay: [
      {
        title: "Blank Holiday Card",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes",
        imgUrl: undefined,
        seasonalType: "mothers-day",
        chips: "---"
      }
    ],
    fathersDay: [
      {
        title: "Blank Holiday Card",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes",
        imgUrl: undefined,
        seasonalType: "fathers-day",
        chips: "---"
      }
    ],
    memorialDay: [
      {
        title: "Blank Holiday Card",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes",
        imgUrl: undefined,
        seasonalType: "memorial-day",
        chips: "---"
      }
    ],
    laborDay: [
      {
        title: "Blank Holiday Card",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes",
        imgUrl: undefined,
        seasonalType: "labor-day",
        chips: "---"
      }
    ],
    veteransDay: [
      {
        title: "Blank Holiday Card",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes",
        imgUrl: undefined,
        seasonalType: "veterans-day",
        chips: "---"
      }
    ],
    earthDay: [
      {
        title: "Blank Holiday Card",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes",
        imgUrl: undefined,
        seasonalType: "earth-day",
        chips: "---"
      }
    ],
    halloween: [
      {
        title: "Blank Holiday Card",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes",
        imgUrl: undefined,
        seasonalType: "halloween",
        chips: "---"
      }
    ],
    summer: [
      {
        title: "Blank Holiday Card",
        prompt: "Blank prompt placeholder",
        description: "Blank card for design purposes",
        imgUrl: undefined,
        seasonalType: "summer",
        chips: "---"
      }
    ]
  };

  return (
    <div className="min-h-screen flex flex-col flex-1 relative">
      {/* Magical Background */}
      <div className="fixed inset-0 -z-10">
        <img 
          src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
          alt="Mystveil Township Map" 
          className="w-full h-full object-cover opacity-30 blur-sm" 
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
      </div>

      {/* Magical Particles */}
      <MysticalParticles />

      {/* Navigation */}
      <Navbar />

      {/* Main Content */}
      <main className="flex-grow px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="mystical-card mb-12">
            <h1 className="mystical-title text-black mb-8">
              Mystveil Upgrades: Blank Cards
            </h1>

            <div className="mystical-card bg-white bg-opacity-95 mb-8">
              <h2 className="mystical-subtitle text-black text-2xl mb-6">
                Design Templates
              </h2>
              <p className="mystical-text text-black">
                This page shows blank card designs for every section and holiday theme.
                Perfect for prototyping and design reference purposes.
              </p>
            </div>
          </div>

          {/* Test Upgrades for Each Section */}
          <div className="mystical-card">
            <div className="text-center mb-8">
              <h2 className="mystical-title text-black mb-4">Blank Cards Collection</h2>
              <p className="mystical-text text-lg">Design Templates for All Sections!</p>
            </div>

            {/* Head Upgrades */}
            <div className="mb-12">
              <SectionHeader title="Head" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {testUpgrades.head.map((upgrade, index) => (
                  <TestCard key={`head-test-${index}`} {...upgrade} />
                ))}
              </div>
            </div>

            {/* Ears Upgrades */}
            <div className="mb-12">
              <SectionHeader title="Ears" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {testUpgrades.ears.map((upgrade, index) => (
                  <TestCard key={`ears-test-${index}`} {...upgrade} />
                ))}
              </div>
            </div>

            {/* Body Upgrades */}
            <div className="mb-12">
              <SectionHeader title="Body" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {testUpgrades.body.map((upgrade, index) => (
                  <TestCard key={`body-test-${index}`} {...upgrade} />
                ))}
              </div>
            </div>

            {/* Legs Upgrades */}
            <div className="mb-12">
              <SectionHeader title="Legs" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {testUpgrades.legs.map((upgrade, index) => (
                  <TestCard key={`legs-test-${index}`} {...upgrade} />
                ))}
              </div>
            </div>

            {/* Tail Upgrades */}
            <div className="mb-12">
              <SectionHeader title="Tail" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {testUpgrades.tail.map((upgrade, index) => (
                  <TestCard key={`tail-test-${index}`} {...upgrade} />
                ))}
              </div>
            </div>

            {/* Markings Upgrades */}
            <div className="mb-12">
              <SectionHeader title="Markings" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {testUpgrades.markings.map((upgrade, index) => (
                  <TestCard key={`markings-test-${index}`} {...upgrade} />
                ))}
              </div>
            </div>


            {/* Misc Upgrades */}
            <div className="mb-12">
              <SectionHeader title="Misc" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {testUpgrades.misc.map((upgrade, index ) => (
                  <TestCard key={`misc-test-${index}`} {...upgrade} />
                ))}
              </div>
            </div>
          </div>

          {/* Holiday Upgrades */}
          <div className="mystical-card mt-12">
            <div className="text-center mb-12">
              <h2 className="mystical-title text-black mb-8">Blank Holiday Card Designs!</h2>
              <p className="mystical-text text-lg">Design templates for every celebration theme.</p>
            </div>

            {/* Thanksgiving Upgrades */}
            <HolidaySection 
              title="THANKSGIVING SPECIALS" 
              seasonalType="thanksgiving" 
              cards={holidayCards.thanksgiving}
              timeLeft={timeLeft}
            />

            {/* Christmas Upgrades */}
            <HolidaySection 
              title="CHRISTMAS MAGIC" 
              seasonalType="christmas" 
              cards={holidayCards.christmas}
              timeLeft={timeLeft}
            />

            {/* New Years Upgrades */}
            <HolidaySection 
              title="NEW YEARS CELEBRATION" 
              seasonalType="new-years" 
              cards={holidayCards.newYears}
              timeLeft={timeLeft}
            />

            {/* Valentines Day Upgrades */}
            <HolidaySection 
              title="VALENTINES DAY HEARTS" 
              seasonalType="valentines" 
              cards={holidayCards.valentines}
              timeLeft={timeLeft}
            />

            {/* Easter Upgrades */}
            <HolidaySection 
              title="EASTER HOPPY" 
              seasonalType="easter" 
              cards={holidayCards.easter}
              timeLeft={timeLeft}
            />

            {/* 4th of July Upgrades */}
            <HolidaySection 
              title="INDEPENDENCE DAY" 
              seasonalType="fourth-of-july" 
              cards={holidayCards.fourthOfJuly}
              timeLeft={timeLeft}
            />

            {/* St. Patrick's Day Upgrades */}
            <HolidaySection 
              title="ST. PATRICK'S DAY" 
              seasonalType="st-patricks" 
              cards={holidayCards.stPatricks}
              timeLeft={timeLeft}
            />

            {/* Mother's Day Upgrades */}
            <HolidaySection 
              title="MOTHER'S DAY" 
              seasonalType="mothers-day" 
              cards={holidayCards.mothersDay}
              timeLeft={timeLeft}
            />

            {/* Father's Day Upgrades */}
            <HolidaySection 
              title="FATHER'S DAY" 
              seasonalType="fathers-day" 
              cards={holidayCards.fathersDay}
              timeLeft={timeLeft}
            />

            {/* Memorial Day Upgrades */}
            <HolidaySection 
              title="MEMORIAL DAY" 
              seasonalType="memorial-day" 
              cards={holidayCards.memorialDay}
              timeLeft={timeLeft}
            />

            {/* Labor Day Upgrades */}
            <HolidaySection 
              title="LABOR DAY" 
              seasonalType="labor-day" 
              cards={holidayCards.laborDay}
              timeLeft={timeLeft}
            />

            {/* Veterans Day Upgrades */}
            <HolidaySection 
              title="VETERANS DAY" 
              seasonalType="veterans-day" 
              cards={holidayCards.veteransDay}
              timeLeft={timeLeft}
            />

            {/* Earth Day Upgrades */}
            <HolidaySection 
              title="EARTH DAY" 
              seasonalType="earth-day" 
              cards={holidayCards.earthDay}
              timeLeft={timeLeft}
            />

            {/* Halloween Upgrades - Original Design */}
            <div className="mb-12 relative">
              {/* Dark Pumpkin Background Effects */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-900/20 via-black/40 to-amber-900/20 rounded-2xl"></div>
              <div className="absolute top-4 left-4 text-2xl opacity-15 text-orange-600">🎃</div>
              <div className="absolute top-4 right-4 text-2xl opacity-15 text-orange-600">🎃</div>
              <div className="absolute bottom-4 left-4 text-2xl opacity-15 text-orange-600">🎃</div>
              <div className="absolute bottom-4 right-4 text-2xl opacity-15 text-orange-600">🎃</div>

              {/* Dark Pumpkin Section Header with Countdown */}
              <div className="relative z-10 mb-8">
                <div className="mystical-card bg-gradient-to-r from-orange-900 via-black to-amber-900 bg-opacity-70 border-2 border-orange-600 shadow-lg shadow-orange-500/20">
                  <h3 className="mystical-subtitle text-orange-100 text-3xl text-center font-bold tracking-wider drop-shadow-lg">
                    🎃 HALLOWEEN BLANK CARDS
                  </h3>
                  
                  {/* Countdown Timer */}
                  <div className="mt-6 grid grid-cols-4 gap-4 max-w-md mx-auto">
                    <div className="text-center">
                      <div className="bg-orange-800/50 rounded-lg p-3 border border-orange-600">
                        <div className="text-orange-100 text-2xl font-bold">{timeLeft.days || 0}</div>
                        <div className="text-orange-200 text-sm">Days</div>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="bg-orange-800/50 rounded-lg p-3 border border-orange-600">
                        <div className="text-orange-100 text-2xl font-bold">{timeLeft.hours || 0}</div>
                        <div className="text-orange-200 text-sm">Hours</div>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="bg-orange-800/50 rounded-lg p-3 border border-orange-600">
                        <div className="text-orange-100 text-2xl font-bold">{timeLeft.minutes || 0}</div>
                        <div className="text-orange-200 text-sm">Minutes</div>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="bg-orange-800/50 rounded-lg p-3 border border-orange-600">
                        <div className="text-orange-100 text-2xl font-bold">{timeLeft.seconds || 0}</div>
                        <div className="text-orange-200 text-sm">Seconds</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Halloween Cards Grid */}
              <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {holidayCards.halloween.map((card, index) => (
                  <div key={`halloween-holiday-${index}`} className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-600/10 via-amber-600/10 to-black/15 rounded-xl blur-sm transition-all duration-300"></div>

                    <div className="relative group transition-all duration-300 border-2 border-orange-600 shadow-lg shadow-orange-500/20 hover:shadow-orange-400/30 backdrop-filter-blur rounded-2xl p-6 bg-gradient-to-br from-orange-900/20 via-black to-amber-900/20">
                      
                      <div className="mb-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full text-center">
                        BLANK CARD
                      </div>
                      
                      <div className="mb-4 flex justify-center relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-orange-600/30 to-amber-600/30 rounded-lg blur-sm"></div>
                        <div className="relative w-48 h-48 bg-gradient-to-br from-orange-900 via-orange-700 to-amber-700 rounded-lg flex items-center justify-center border-2 border-orange-300 shadow-lg filter brightness-90 sepia-10">
                          <span className="text-6xl text-orange-100">🎃</span>
                        </div>
                      </div>
                      
                      <h4 className="mystical-subtitle text-orange-100 text-xl mb-3 font-bold text-center drop-shadow-md">
                        {card.title}
                      </h4>
                      
                      {card.prompt && (
                        <div className="mystical-card bg-orange-800 bg-opacity-40 border border-orange-600 mb-3">
                          <p className="mystical-text text-orange-200 text-sm italic leading-relaxed">
                            [ {card.prompt} ]
                          </p>
                        </div>
                      )}
                      
                      {card.description && (
                        <p className="mystical-text text-orange-200 mb-4 text-sm leading-relaxed text-center">
                          {card.description}
                        </p>
                      )}
                      
                      {card.chips && (
                        <div className="mystical-card bg-gradient-to-r from-orange-700 to-amber-700 bg-opacity-60 border border-orange-500">
                          <p className="mystical-text text-orange-100 font-bold text-center">
                              🎃 Chips: {card.chips} 🎃  
                          </p>
                        </div>
                      )}
                      
                      <div className="mt-4 text-center">
                        <p className="mystical-text text-orange-300 font-semibold text-lg drop-shadow-md">
                          🎃 Blank Halloween Design 🎃
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="relative z-10 mt-8 text-center">
                <p className="text-orange-300 font-semibold text-lg drop-shadow-md">
                  🎃 Blank Halloween Design Template! 🎃
                </p>
              </div>
            </div>

            {/* Summer Vacation Upgrades */}
            <HolidaySection 
              title="SUMMER VIBES" 
              seasonalType="summer" 
              cards={holidayCards.summer}
              timeLeft={timeLeft}
            />
          </div>

          <div className="flex justify-center mt-12">
            <Link to="/upgrades" className="mystical-button">
              View Regular Upgrades
            </Link>
          </div>
        </div>
      </main>

      {/* Mystical Footer */}
      <footer className="mystical-nav py-6 mt-auto">
        <div className="container mx-auto px-4 text-center">
          <p className="mystical-text font-bold">
            © 2025 4fingerstudios <br /> 
            © 2024 Reckless Dreams. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MystveilUpgradesDevsPage;

